import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button, Spinner } from "../ui";
import { previewReportChart, type ChartPreviewResponse } from "../../services/reportEditApi";

const GANTT_CHART_ID = "gantt";

type Props = {
  chartId: string;
  payload: unknown;
  reportHtml: string;
  disabled?: boolean;
  chartLabel?: string;
  /** Full width preview panel (slot cards). */
  fullWidth?: boolean;
};

export default function ReportChartPreview({
  chartId,
  payload,
  reportHtml,
  disabled = false,
  chartLabel,
  fullWidth = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<ChartPreviewResponse | null>(null);
  const isOpen = Boolean(preview?.svg);

  useEffect(() => {
    setPreview(null);
    setError("");
  }, [chartId]);

  const loadPreview = useCallback(async () => {
    const id = chartId?.trim();
    if (!id || payload == null) {
      setError("Select a chart to preview.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await previewReportChart({
        chartId: id,
        payload,
        html: id === GANTT_CHART_ID ? reportHtml : undefined,
      });
      setPreview(result);
    } catch (err) {
      setPreview(null);
      const message =
        err instanceof Error ? err.message : "Could not load chart preview.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [chartId, payload, reportHtml]);

  const handleToggle = () => {
    if (isOpen) {
      setPreview(null);
      setError("");
      return;
    }
    void loadPreview();
  };

  const title = preview?.label || chartLabel || chartId;

  return (
    <div
      className={`sme-chart-preview${fullWidth ? " sme-chart-preview--full" : ""}${
        isOpen ? " sme-chart-preview--open" : ""
      }`}
    >
      <Button
        type="button"
        size="sm"
        variant={isOpen ? "secondary" : "outline"}
        disabled={disabled || loading || !chartId?.trim()}
        isLoading={loading}
        onClick={handleToggle}
        className="sme-chart-preview__toggle"
      >
        {isOpen ? (
          <EyeOff className="sme-chart-preview__icon" aria-hidden />
        ) : (
          <Eye className="sme-chart-preview__icon" aria-hidden />
        )}
        {isOpen ? "Hide preview" : "Preview chart"}
      </Button>

      {loading ? (
        <div className="sme-chart-preview__loading" role="status">
          <Spinner size="sm" />
          <span>Drawing preview…</span>
        </div>
      ) : null}

      {error ? <p className="sme-chart-preview__error m-0">{error}</p> : null}

      {isOpen && preview?.svg ? (
        <figure className="sme-chart-preview__figure">
          <figcaption className="sme-chart-preview__caption">{title}</figcaption>
          <div
            className="sme-chart-preview__box"
            style={{ fontFamily: "Inter, Arial, sans-serif" }}
            dangerouslySetInnerHTML={{ __html: preview.svg }}
          />
        </figure>
      ) : null}
    </div>
  );
}
